import type {
  AdminTicketDto, ContentDto, OpsCueDto, OpsDockSummaryDto, OpsEventSettingsDto, OpsRiskDto,
  OpsStaffSummaryDto, OpsTaskDto, PaymentAccountDto, SalesChannelDto, SeatZoneDto, TicketDeliveryDto,
} from './api';

/**
 * TICK-135 — what "ready" counts as, per phase. Closes TICK-120 §F-9.
 *
 * The approved mockup shows `78%` and `5/6 พร้อม` with nothing behind them. This file is the
 * behind. `docs/ops/TICK-135_READINESS_DEFINITION.md` carries the reasoning; the six rules it
 * settles are the six rules this file implements, and the tests pin each one by name:
 *
 *   1. readiness is a checklist, not a score — every check is one yes/no a query already answers.
 *   2. it measures what a person had to configure, never the state of a queue. Nothing here moves
 *      because a buyer uploaded a slip or because the clock advanced; the "ต้องทำต่อ" list on the
 *      same screen is where live work belongs, and mixing the two makes a number that flickers.
 *   3. "ready" is the blocking checks, not the percentage. A phase can be ready at 71% and not
 *      ready at 88%, which is the point: nobody has to answer "is 78% ready enough".
 *   4. a check that does not apply leaves the denominator — counting it as passed hands free marks
 *      to the event that configured less. A check only exists where the data can tell "not done
 *      yet" from "not wanted": wristbands cannot, so there is no wristband check.
 *   5. what the token cannot read is not zero and not ready — it is "seen in part".
 *   6. the event figure is passed ÷ applicable across phases, never the mean of phase percentages,
 *      which would let a 3-check phase outvote a 7-check one.
 *
 * Pure by design: DTOs in, structure out, no fetch and no React. Everything above is testable as
 * data, and the panel is left with rendering.
 */

/** TICK-135 — likelihood and impact are each 1–5, so 12 is the floor where *both* are above the middle. */
export const HIGH_RISK_SCORE = 12;

/** Where a failed check is fixed. A subset of the shell's `MainTab`, which is not exported. */
export type ReadinessTab =
  | 'tickets' | 'payments' | 'content' | 'seatplan' | 'delivery' | 'channels'
  | 'opsSettings' | 'opsPeople' | 'opsTasks' | 'opsRisks' | 'opsShowday';

export type CheckStatus = 'pass' | 'fail' | 'not_applicable' | 'unreadable';

export interface ReadinessCheck {
  id: string;
  label: string;
  /** A failed blocking check means the phase is not ready, whatever the percentage says. */
  blocking: boolean;
  status: CheckStatus;
  /** What is missing, in the numbers the check counted. Empty when it passed. */
  detail: string;
  tab: ReadinessTab;
}

export type PhaseKey = 'setup' | 'sell' | 'prepare' | 'showday';

export type PhaseState =
  /** Every blocking check passes. */
  | 'ready'
  /** A blocking check fails. */
  | 'not_ready'
  /** A blocking check could not be read with this token, so neither answer can be given. */
  | 'partial_view'
  /** Nothing in the phase could be read at all. */
  | 'no_access';

export interface PhaseReadiness {
  key: PhaseKey;
  /** Matches the lifecycle menu heading, so a card and a menu group say the same words. */
  label: string;
  checks: ReadinessCheck[];
  passed: number;
  applicable: number;
  /** Null when nothing in the phase applies to this event, or nothing could be read. */
  percent: number | null;
  state: PhaseState;
}

export interface EventReadiness {
  phases: PhaseReadiness[];
  passed: number;
  applicable: number;
  percent: number | null;
  /** The mockup's `5/6 พร้อม`: phases whose blocking checks all pass, over phases this token sees. */
  readyPhases: number;
  visiblePhases: number;
  /** True when any phase hid a blocking check behind a permission — the figure is of a partial view. */
  partial: boolean;
}

/**
 * Every read the checks need. `null` means *this token may not read it* — a 403, or a request the
 * caller never made because it knew the answer would be 403. It never means "the request failed":
 * a real failure is an error on the screen, not a quietly incomplete readiness figure.
 */
export interface ReadinessInput {
  content: ContentDto | null;
  tickets: AdminTicketDto[] | null;
  paymentAccounts: PaymentAccountDto[] | null;
  seatZones: SeatZoneDto[] | null;
  ticketDelivery: TicketDeliveryDto | null;
  salesChannels: SalesChannelDto[] | null;
  opsSettings: OpsEventSettingsDto | null;
  staff: OpsStaffSummaryDto[] | null;
  tasks: OpsTaskDto[] | null;
  risks: OpsRiskDto[] | null;
  cues: OpsCueDto[] | null;
  docks: OpsDockSummaryDto[] | null;
}

const PHASE_LABEL: Record<PhaseKey, string> = {
  setup: '02 · ตั้งค่าอีเวนต์',
  sell: '03 · ขายและส่งมอบ',
  prepare: '04 · เตรียมงาน',
  showday: '05 · วันแสดง',
};

/** What a phase promises when its blocking checks pass — the sentence the card puts under its name. */
export const PHASE_MEANING: Record<PhaseKey, string> = {
  setup: 'คนแปลกหน้าซื้อบัตรได้',
  sell: 'เงินที่เข้ามาแล้ว ตั๋วออกไปได้',
  prepare: 'ทีมงานทำงานจากแผนได้',
  showday: 'รันงานจากคอนโซลได้',
};

type Spec = {
  id: string;
  label: string;
  blocking?: true;
  tab: ReadinessTab;
  /**
   * `null` — this token cannot read what the check needs.
   * `'n/a'` — the check does not apply to this event, so it leaves both sides of the fraction.
   * `true` / a failure string — passed, or failed with what was counted.
   */
  run: (i: ReadinessInput) => null | 'n/a' | true | string;
};

const blank = (s: string | null | undefined) => !s || s.trim() === '';

const SETUP: Spec[] = [
  {
    id: 'tickets_on_sale',
    label: 'มีประเภทบัตรที่เปิดขายอย่างน้อยหนึ่งประเภท',
    blocking: true,
    tab: 'tickets',
    run: ({ tickets }) => {
      if (!tickets) return null;
      const open = tickets.filter((t) => t.visible && !t.closed);
      return open.length > 0 || `มี ${tickets.length} ประเภท แต่ไม่มีประเภทไหนเปิดขายอยู่`;
    },
  },
  {
    id: 'payment_account',
    label: 'มีบัญชีรับเงินที่เปิดใช้งาน',
    blocking: true,
    tab: 'payments',
    run: ({ paymentAccounts }) => {
      if (!paymentAccounts) return null;
      return paymentAccounts.some((a) => a.enabled)
        || (paymentAccounts.length === 0 ? 'ยังไม่มีบัญชีรับเงิน' : `มี ${paymentAccounts.length} บัญชี แต่ปิดใช้งานทั้งหมด`);
    },
  },
  {
    id: 'event_name',
    label: 'หน้า Landing มีชื่องาน',
    blocking: true,
    tab: 'content',
    run: ({ content }) => (content ? !blank(content.name) || 'ยังไม่ได้ตั้งชื่องาน' : null),
  },
  {
    id: 'ticket_capacity',
    label: 'บัตรที่เปิดขายทุกประเภทมีความจุมากกว่า 0',
    tab: 'tickets',
    run: ({ tickets }) => {
      if (!tickets) return null;
      const open = tickets.filter((t) => t.visible && !t.closed);
      if (open.length === 0) return 'n/a';
      const empty = open.filter((t) => t.capacity <= 0);
      return empty.length === 0 || `${empty.length} ประเภทยังไม่มีความจุ (${empty.map((t) => t.nameTh || t.nameEn).join(' · ')})`;
    },
  },
  {
    id: 'seat_zones',
    label: 'บัตรแบบเลือกที่นั่งทุกประเภทมีโซนที่นั่งแล้ว',
    tab: 'seatplan',
    run: ({ tickets, seatZones }) => {
      if (!tickets) return null;
      const seated = tickets.filter((t) => t.seatingMode === 'zone_seated');
      if (seated.length === 0) return 'n/a';
      if (!seatZones) return null;
      const without = seated.filter((t) => !seatZones.some((z) => z.ticketId === t.id));
      return without.length === 0 || `${without.length} ประเภทยังไม่มีโซน (${without.map((t) => t.nameTh || t.nameEn).join(' · ')})`;
    },
  },
  {
    id: 'hero_image',
    label: 'หน้า Landing มีภาพหลัก',
    tab: 'content',
    run: ({ content }) => (content ? content.heroImageKey !== null || 'ยังไม่ได้อัปโหลดภาพหลัก' : null),
  },
  {
    id: 'contact_email',
    label: 'หน้า Landing มีอีเมลติดต่อ',
    tab: 'content',
    run: ({ content }) => (content ? !blank(content.contactEmail) || 'ยังไม่ได้ใส่อีเมลติดต่อ' : null),
  },
];

/** Copy channels the owner ticked that the platform can actually send on right now. */
const liveCopyChannels = (d: TicketDeliveryDto) => d.copyChannels.filter((c) => d.channelAvailability[c]);

const SELL: Spec[] = [
  {
    id: 'ticket_reachable',
    label: 'ผู้ซื้อที่จ่ายแล้วมีทางได้รับตั๋ว',
    blocking: true,
    tab: 'delivery',
    run: ({ ticketDelivery }) => {
      if (!ticketDelivery) return null;
      if (ticketDelivery.myTicketPortalEnabled) return true;
      const live = ticketDelivery.mode === 'instant_and_send' ? liveCopyChannels(ticketDelivery) : [];
      return live.length > 0
        || 'พอร์ทัลตั๋วปิดอยู่ และไม่มีช่องทางสำเนาที่ส่งได้ — คนที่จ่ายเงินแล้วจะไม่มีทางเห็นตั๋วของตัวเอง';
    },
  },
  {
    id: 'delivery_channels_live',
    label: 'ช่องทางสำเนาที่เลือกไว้ ส่งได้จริงทุกช่อง',
    blocking: true,
    tab: 'delivery',
    run: ({ ticketDelivery }) => {
      if (!ticketDelivery) return null;
      if (ticketDelivery.mode !== 'instant_and_send') return 'n/a';
      const dead = ticketDelivery.copyChannels.filter((c) => !ticketDelivery.channelAvailability[c]);
      return dead.length === 0 || `เลือกไว้แต่ส่งไม่ได้: ${dead.join(' · ')}`;
    },
  },
  {
    id: 'auto_verify_ready',
    label: 'บัญชีรับเงินที่เปิดอยู่ตั้งค่าครบพอให้ตรวจสลิปอัตโนมัติ',
    tab: 'payments',
    run: ({ paymentAccounts }) => {
      if (!paymentAccounts) return null;
      const on = paymentAccounts.filter((a) => a.enabled);
      if (on.length === 0) return 'n/a';
      const noCode = on.filter((a) => a.bankCode === null);
      return noCode.length === 0
        || `${noCode.length} บัญชียังไม่มีรหัสธนาคาร — สลิปของบัญชีนั้นต้องตรวจด้วยคน`;
    },
  },
  {
    id: 'sales_channel',
    label: 'มีช่องทางการขายอย่างน้อยหนึ่งช่อง',
    tab: 'channels',
    run: ({ salesChannels }) => {
      if (!salesChannels) return null;
      const live = salesChannels.filter((c) => !(c.archivedAt ?? c.archived_at));
      return live.length > 0 || 'ยังไม่มีช่องทาง — ยอดขายจะไม่รู้ว่ามาจากใคร';
    },
  },
];

const taskHasOwner = (t: OpsTaskDto) => t.ownerStaffId !== null || !blank(t.importedOwnerName);

const PREPARE: Spec[] = [
  {
    id: 'target_revenue',
    label: 'ตั้งเป้ารายได้ของงานแล้ว',
    blocking: true,
    tab: 'opsSettings',
    run: ({ opsSettings }) => (opsSettings ? opsSettings.targetRevenue !== null || 'ยังไม่ได้ตั้งเป้ารายได้' : null),
  },
  {
    id: 'has_tasks',
    label: 'มีแผนงานอย่างน้อยหนึ่งงาน',
    blocking: true,
    tab: 'opsTasks',
    run: ({ tasks }) => (tasks ? tasks.length > 0 || 'ยังไม่มีงานในแผนเลย' : null),
  },
  {
    id: 'task_owners',
    label: 'ทุกงานมีเจ้าของ',
    blocking: true,
    tab: 'opsTasks',
    run: ({ tasks }) => {
      if (!tasks) return null;
      if (tasks.length === 0) return 'n/a';
      const orphan = tasks.filter((t) => !taskHasOwner(t));
      return orphan.length === 0 || `${orphan.length} งานยังไม่มีคนรับผิดชอบ`;
    },
  },
  {
    id: 'high_risk_handled',
    label: `ความเสี่ยงคะแนน ≥ ${HIGH_RISK_SCORE} ทุกเรื่อง มีแผนรับมือหรือถูกลงนามยอมรับ`,
    blocking: true,
    tab: 'opsRisks',
    run: ({ risks }) => {
      if (!risks) return null;
      const high = risks.filter((r) => r.score >= HIGH_RISK_SCORE);
      if (high.length === 0) return 'n/a';
      const bare = high.filter((r) => blank(r.mitigationPlan) && r.signoffs.length === 0);
      return bare.length === 0
        || `${bare.length} เรื่องยังไม่มีทั้งแผนรับมือและการลงนาม (${bare.map((r) => r.title).join(' · ')})`;
    },
  },
  {
    id: 'has_crew',
    label: 'มีทีมงานอย่างน้อยหนึ่งคน',
    tab: 'opsPeople',
    run: ({ staff }) => (staff ? staff.length > 0 || 'ยังไม่มีรายชื่อทีมงาน' : null),
  },
  {
    id: 'crew_departments',
    label: 'ทุกคนในทีมสังกัดอย่างน้อยหนึ่งฝ่าย',
    tab: 'opsPeople',
    run: ({ staff }) => {
      if (!staff) return null;
      if (staff.length === 0) return 'n/a';
      const loose = staff.filter((s) => s.departments.length === 0);
      return loose.length === 0 || `${loose.length} คนยังไม่มีฝ่าย`;
    },
  },
  {
    id: 'risk_owners',
    label: 'ความเสี่ยงที่ยังเปิดอยู่ทุกเรื่องมีเจ้าของ',
    tab: 'opsRisks',
    run: ({ risks }) => {
      if (!risks) return null;
      const open = risks.filter((r) => r.status === 'open' || r.status === 'mitigating');
      if (open.length === 0) return 'n/a';
      const orphan = open.filter((r) => r.ownerStaffId === null);
      return orphan.length === 0 || `${orphan.length} เรื่องยังไม่มีเจ้าของ`;
    },
  },
];

const SHOWDAY: Spec[] = [
  {
    id: 'has_cues',
    label: 'มีคิวบนเวทีอย่างน้อยหนึ่งคิว',
    blocking: true,
    tab: 'opsShowday',
    run: ({ cues }) => (cues ? cues.length > 0 || 'ยังไม่มีลำดับคิวในระบบ' : null),
  },
  {
    id: 'cue_owners',
    label: 'ทุกคิวมีเจ้าของ',
    blocking: true,
    tab: 'opsShowday',
    run: ({ cues }) => {
      if (!cues) return null;
      if (cues.length === 0) return 'n/a';
      const orphan = cues.filter((c) => c.ownerStaffId === null);
      return orphan.length === 0 || `${orphan.length} คิวยังไม่มีคนรับผิดชอบ`;
    },
  },
  {
    id: 'dock_details',
    label: 'รอบลงของทุกรอบมีเวลานัดและทะเบียนรถ',
    tab: 'opsShowday',
    run: ({ docks }) => {
      if (!docks) return null;
      if (docks.length === 0) return 'n/a';
      const thin = docks.filter((d) => blank(d.scheduledTime) || blank(d.truckPlate));
      return thin.length === 0 || `${thin.length} รอบยังกรอกไม่ครบ`;
    },
  },
];

const PHASE_SPECS: Record<PhaseKey, Spec[]> = {
  setup: SETUP, sell: SELL, prepare: PREPARE, showday: SHOWDAY,
};

/**
 * One phase's checks, for a screen that owns that phase and should not pay for the other three.
 *
 * The Command Center wants the whole event and reads twelve endpoints for it. A destination inside
 * a phase — On-site Settings inside `prepare` — needs only the reads its own phase's specs touch,
 * and passing `null` for the rest is honest rather than lossy: a spec that never looks at a field
 * cannot be affected by it, and no spec outside its own phase runs here at all.
 */
export function computePhaseReadiness(key: PhaseKey, input: ReadinessInput): PhaseReadiness {
  return runPhase(key, input);
}

function runPhase(key: PhaseKey, input: ReadinessInput): PhaseReadiness {
  const checks: ReadinessCheck[] = PHASE_SPECS[key].map((spec) => {
    const outcome = spec.run(input);
    const status: CheckStatus = outcome === null ? 'unreadable'
      : outcome === 'n/a' ? 'not_applicable'
        : outcome === true ? 'pass' : 'fail';
    return {
      id: spec.id,
      label: spec.label,
      blocking: spec.blocking === true,
      status,
      detail: status === 'fail' ? String(outcome) : '',
      tab: spec.tab,
    };
  });

  // Rule 4: only pass and fail are counted. `not_applicable` and `unreadable` leave the fraction.
  const applicable = checks.filter((c) => c.status === 'pass' || c.status === 'fail').length;
  const passed = checks.filter((c) => c.status === 'pass').length;

  const blocking = checks.filter((c) => c.blocking);
  const state: PhaseState = checks.every((c) => c.status === 'unreadable') ? 'no_access'
    // Rule 5: a blocking check nobody could read makes both answers unavailable, not one of them.
    : blocking.some((c) => c.status === 'unreadable') ? 'partial_view'
      // Rule 3: readiness is the blocking checks, never the percentage.
      : blocking.every((c) => c.status !== 'fail') ? 'ready' : 'not_ready';

  return {
    key,
    label: PHASE_LABEL[key],
    checks,
    passed,
    applicable,
    percent: applicable === 0 ? null : Math.round((passed / applicable) * 100),
    state,
  };
}

/**
 * TICK-148 — the phases whose blocking checks decide whether a stranger can buy and be sent a
 * ticket. A new owner whose event fails one of these has nothing to sell yet, which is the only
 * case where the shell opens on the Command Center instead of the slip queue.
 */
export const SELLING_PHASES: readonly PhaseKey[] = ['setup', 'sell'];

/**
 * TICK-148 — blocking checks that were read and failed, in phase order.
 *
 * Only `fail` counts. An `unreadable` check is rule 5's "seen in part": the token could not look,
 * so saying it failed would be as wrong as saying it passed. `not_applicable` and `pass` have
 * nothing to fix. Pass `phases` to narrow to some of them.
 */
export function actionableBlockers(
  readiness: EventReadiness | null,
  phases?: readonly PhaseKey[],
): ReadinessCheck[] {
  if (!readiness) return [];
  return readiness.phases
    .filter((p) => !phases || phases.includes(p.key))
    .flatMap((p) => p.checks.filter((c) => c.blocking && c.status === 'fail'));
}

/** TICK-148 — true when every phase was fully readable, so "nothing failed" can be said as fact. */
export function fullyReadable(readiness: EventReadiness | null): boolean {
  return readiness !== null
    && readiness.visiblePhases > 0
    && readiness.phases.every((p) => p.state !== 'partial_view' && p.state !== 'no_access');
}

export function computeReadiness(input: ReadinessInput): EventReadiness {
  const phases = (Object.keys(PHASE_SPECS) as PhaseKey[]).map((k) => runPhase(k, input));
  const visible = phases.filter((p) => p.state !== 'no_access');

  // Rule 6: one fraction over every check, not the mean of four fractions.
  const passed = visible.reduce((n, p) => n + p.passed, 0);
  const applicable = visible.reduce((n, p) => n + p.applicable, 0);

  return {
    phases,
    passed,
    applicable,
    percent: applicable === 0 ? null : Math.round((passed / applicable) * 100),
    readyPhases: visible.filter((p) => p.state === 'ready').length,
    visiblePhases: visible.length,
    partial: visible.some((p) => p.state === 'partial_view'),
  };
}
